import { useState } from 'react';
import { useContent } from '../../../contexts/ContentContext';
import { useNavigate } from 'react-router-dom';

export default function AdminNotesPage() {
  const { notes, deleteNote } = useContent();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('全部');
  const [sortBy, setSortBy] = useState<'date' | 'likes'>('date');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const categories = ['全部', ...Array.from(new Set(notes.map(n => n.category)))];

  const filteredNotes = notes
    .filter(note => {
      const matchSearch = note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        note.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCategory = categoryFilter === '全部' || note.category === categoryFilter;
      return matchSearch && matchCategory;
    })
    .sort((a, b) => {
      if (sortBy === 'date') {
        return b.id - a.id;
      }
      return b.likes - a.likes;
    });

  const handleDelete = (id: number) => {
    deleteNote(id);
    setDeleteConfirm(null);
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">笔记管理</h1>
          <p className="text-gray-600 mt-2">管理所有笔记内容</p>
        </div>
        <button
          onClick={() => navigate('/admin/notes/new')}
          className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all duration-300 flex items-center gap-2 whitespace-nowrap"
        >
          <i className="ri-add-line text-xl"></i>
          新建笔记
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[300px]">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
              <input
                type="text"
                placeholder="搜索笔记标题或内容..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
              />
            </div>
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm cursor-pointer"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'date' | 'likes')}
            className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm cursor-pointer"
          >
            <option value="date">按日期排序</option>
            <option value="likes">按点赞数排序</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredNotes.map(note => (
          <div key={note.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
            {note.image && (
              <img
                src={note.image}
                alt={note.title}
                className="w-full h-48 object-cover"
              />
            )}
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <span className={`px-3 py-1 ${note.categoryColor} rounded-full text-sm whitespace-nowrap`}>
                  {note.category}
                </span>
                {note.isPinned && (
                  <i className="ri-pushpin-fill text-purple-500"></i>
                )}
              </div>

              <h3 className="font-bold text-gray-900 mb-2 line-clamp-2">{note.title}</h3>
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">{note.excerpt}</p>

              <div className="flex items-center gap-2 mb-4">
                <img
                  src={note.authorAvatar}
                  alt={note.author}
                  className="w-6 h-6 rounded-full"
                />
                <span className="text-sm text-gray-600">{note.author}</span>
                <span className="text-sm text-gray-400">·</span>
                <span className="text-sm text-gray-400">{note.date}</span>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <i className="ri-heart-line"></i>
                    {note.likes}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i>
                    {note.comments}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/admin/notes/edit/${note.id}`)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="编辑"
                  >
                    <i className="ri-edit-line"></i>
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(note.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="删除"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredNotes.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <i className="ri-sticky-note-line text-6xl text-gray-300 mb-4"></i>
          <p className="text-gray-500">暂无笔记</p>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <i className="ri-error-warning-line text-2xl text-red-600"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">确认删除</h3>
                <p className="text-sm text-gray-600">此操作无法撤销</p>
              </div>
            </div>
            <p className="text-gray-700 mb-6">确定要删除这条笔记吗？</p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
              >
                确认删除
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}